import { useEffect, useState } from 'react';

import type { CatalogClient } from '../../catalog/catalog-client';
import { WEB_CATALOG } from '../../catalog/generated-catalog';
import type { AppStorage } from '../../storage/app-storage';

interface StorageSnapshot {
  readonly persisted: boolean | null;
  readonly quota: number | null;
  readonly usage: number | null;
}

const EMPTY_STORAGE_SNAPSHOT: StorageSnapshot = {
  persisted: null,
  quota: null,
  usage: null,
};

type ReadinessState =
  | {
      readonly client: CatalogClient;
      readonly dataRevision: number;
      readonly error: string;
      readonly status: 'error';
      readonly storage: AppStorage;
    }
  | {
      readonly client: CatalogClient;
      readonly dataRevision: number;
      readonly status: 'ready';
      readonly storage: AppStorage;
      readonly storageSnapshot: StorageSnapshot;
    };

/**
 * Prewarms and verifies the immutable catalog, then gives the user an explicit
 * best-effort way to ask the browser not to evict this origin's offline data.
 */
export function CatalogReadinessPanel({
  client,
  dataRevision,
  storage,
}: {
  readonly client: CatalogClient;
  readonly dataRevision: number;
  readonly storage: AppStorage;
}) {
  const [readiness, setReadiness] = useState<ReadinessState | null>(null);
  const [requestingPersistence, setRequestingPersistence] = useState(false);
  const [persistenceMessage, setPersistenceMessage] = useState<string | null>(null);
  const currentReadiness =
    readiness?.client === client &&
    readiness.dataRevision === dataRevision &&
    readiness.storage === storage
      ? readiness
      : null;
  const ready = currentReadiness?.status === 'ready';
  const error = currentReadiness?.status === 'error' ? currentReadiness.error : null;
  const storageSnapshot =
    currentReadiness?.status === 'ready'
      ? currentReadiness.storageSnapshot
      : EMPTY_STORAGE_SNAPSHOT;

  useEffect(() => {
    let active = true;
    void Promise.all([client.prepare(), storage.listShifts(), storage.listRouteTemplates()])
      .then(async ([, shifts, templates]) => {
        const recentByLocationId = new Map<string, number>();
        for (const aggregate of shifts) {
          for (const segment of aggregate.segments) {
            if (segment.metadata.tombstone !== null) continue;
            const lastUsedAt = Date.parse(segment.metadata.updated_at);
            for (const endpoint of [segment.origin, segment.destination]) {
              if (endpoint === null || !Number.isFinite(lastUsedAt)) continue;
              recentByLocationId.set(
                endpoint.location_id,
                Math.max(recentByLocationId.get(endpoint.location_id) ?? 0, lastUsedAt),
              );
            }
          }
        }
        await client.setUsage({
          favoriteLocationIds: templates
            .filter((template) => template.favorite)
            .flatMap((template) => [template.origin_location_id, template.destination_location_id]),
          recents: [...recentByLocationId]
            .sort(([, left], [, right]) => right - left)
            .map(([locationId, lastUsedAtEpochMs]) => ({
              locationId,
              lastUsedAtEpochMs,
            })),
        });
        if (!active) return;
        const nextStorageSnapshot = await readStorageSnapshot();
        if (!active) return;
        setReadiness({
          client,
          dataRevision,
          status: 'ready',
          storage,
          storageSnapshot: nextStorageSnapshot,
        });
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setReadiness({
          client,
          dataRevision,
          error:
            reason instanceof Error
              ? `Katalog není připravený: ${reason.message}`
              : 'Katalog se nepodařilo připravit.',
          status: 'error',
          storage,
        });
      });
    return () => {
      active = false;
    };
  }, [client, dataRevision, storage]);

  const requestPersistence = async () => {
    setRequestingPersistence(true);
    setPersistenceMessage(null);
    try {
      if (navigator.storage?.persist === undefined) {
        setPersistenceMessage('Tento prohlížeč neumí výslovně požádat o trvalé offline uložení.');
        return;
      }
      const persisted = await navigator.storage.persist();
      const nextStorageSnapshot = await readStorageSnapshot();
      setReadiness((current) =>
        current?.status === 'ready' &&
        current.client === client &&
        current.dataRevision === dataRevision &&
        current.storage === storage
          ? { ...current, storageSnapshot: nextStorageSnapshot }
          : current,
      );
      setPersistenceMessage(
        persisted
          ? 'Prohlížeč potvrdil trvalejší ochranu offline dat tohoto profilu.'
          : 'Prohlížeč ochranu nepotvrdil. Katalog zůstává v cache, ale může být při nedostatku místa odstraněn.',
      );
    } catch (reason) {
      setPersistenceMessage(
        reason instanceof Error
          ? `Požadavek na ochranu offline dat selhal: ${reason.message}`
          : 'Požadavek na ochranu offline dat selhal.',
      );
    } finally {
      setRequestingPersistence(false);
    }
  };

  return (
    <aside className="catalog-readiness" aria-label="Offline katalog provozních míst">
      <strong>Offline katalog CZ + DE</strong>
      {error ? (
        <p role="alert">{error} Ruční zadání místa zůstává dostupné.</p>
      ) : ready ? (
        <>
          <p role="status">
            Připraveno offline: {WEB_CATALOG.documentCount.toLocaleString('cs-CZ')} míst pro Česko a
            Německo. Nedávná místa se nabízejí ještě před psaním.
          </p>
          <p className="field-description">{storageSummary(storageSnapshot)}</p>
          {storageSnapshot.persisted === true ? null : (
            <button
              type="button"
              className="secondary-action touch-target"
              disabled={requestingPersistence}
              onClick={() => void requestPersistence()}
            >
              {requestingPersistence ? 'Žádám prohlížeč…' : 'Chránit offline data před uvolněním'}
            </button>
          )}
          {persistenceMessage ? <p role="status">{persistenceMessage}</p> : null}
        </>
      ) : (
        <p role="status">Ověřuji a připravuji katalog mimo hlavní editor…</p>
      )}
    </aside>
  );
}

async function readStorageSnapshot(): Promise<StorageSnapshot> {
  if (navigator.storage === undefined) return EMPTY_STORAGE_SNAPSHOT;
  const [persisted, estimate] = await Promise.all([
    navigator.storage.persisted?.().catch(() => false) ?? Promise.resolve(null),
    navigator.storage.estimate?.().catch((): StorageEstimate => ({})) ??
      Promise.resolve<StorageEstimate>({}),
  ]);
  return {
    persisted,
    quota: typeof estimate.quota === 'number' ? estimate.quota : null,
    usage: typeof estimate.usage === 'number' ? estimate.usage : null,
  };
}

function storageSummary(snapshot: StorageSnapshot): string {
  const protection =
    snapshot.persisted === true
      ? 'Prohlížeč potvrdil ochranu úložiště před automatickým uvolněním.'
      : snapshot.persisted === false
        ? 'Úložiště zatím nemá potvrzenou ochranu před automatickým uvolněním.'
        : 'Stav ochrany úložiště prohlížeč neposkytl.';
  return protection;
}
